import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  clearThemeOverride,
  getThemeDatasetValue,
  normalizeTheme,
  readThemeOverride,
  resolveEffectiveTheme,
  THEME_DARK,
  THEME_LIGHT,
  writeThemeOverride,
} from "./theme.utils";

const ThemeContext = React.createContext(null);

export const ThemeProvider = ({ children }) => {
  const userTheme = useSelector((state) => state.user?.theme);

  const normalizedUserTheme = React.useMemo(
    () => normalizeTheme(userTheme),
    [userTheme]
  );

  const [savedTheme, setSavedTheme] = React.useState(normalizedUserTheme);
  const [themeOverride, setThemeOverride] = React.useState(readThemeOverride);

  React.useEffect(() => {
    const shouldReset =
        globalThis?.sessionStorage?.getItem("resetThemeAfterAuth") === "1";

    if (shouldReset) {
      clearThemeOverride();
      setThemeOverride(null);
      globalThis?.sessionStorage?.removeItem("resetThemeAfterAuth");
    }
  }, []);
  
  React.useEffect(() => {
    setSavedTheme(normalizedUserTheme);
  }, [normalizedUserTheme]);

  const effectiveTheme = React.useMemo(
    () =>
      resolveEffectiveTheme({
        savedTheme,
        themeOverride,
      }),
    [savedTheme, themeOverride]
  );

  React.useLayoutEffect(() => {
    const root = globalThis?.document?.documentElement;
    if (!root) {
      return;
    }

    const datasetTheme = getThemeDatasetValue(effectiveTheme);
    root.dataset.theme = datasetTheme;
    root.style.colorScheme = datasetTheme;
  }, [effectiveTheme]);

  const updateSavedTheme = React.useCallback((theme) => {
    setSavedTheme(normalizeTheme(theme));
  }, []);

  const clearThemeOverrideState = React.useCallback(() => {
    clearThemeOverride();
    setThemeOverride(null);
  }, []);

  const updateThemeOverride = React.useCallback((theme) => {
    const normalizedTheme = normalizeTheme(theme);
    writeThemeOverride(normalizedTheme);
    setThemeOverride(normalizedTheme);
  }, []);

  const toggleTheme = React.useCallback(() => {
    const nextTheme = effectiveTheme === THEME_DARK ? THEME_LIGHT : THEME_DARK;
    updateThemeOverride(nextTheme);
  }, [effectiveTheme, updateThemeOverride]);

  const value = React.useMemo(
    () => ({
      savedTheme,
      themeOverride,
      effectiveTheme,
      setSavedTheme: updateSavedTheme,
      setThemeOverride: updateThemeOverride,
      toggleTheme,
      clearThemeOverride: clearThemeOverrideState,
    }),
    [
      clearThemeOverrideState,
      effectiveTheme,
      savedTheme,
      updateSavedTheme,
      updateThemeOverride,
      themeOverride,
      toggleTheme,
    ]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useTheme = () => {
  const context = React.useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};
